import { Injectable } from '@angular/core';
import { SESSION_STATUS } from '@app/core/constants/core.constants';
import { UploadSessionItem } from '../../../modules/reconciliation-manager/models';


@Injectable({
  providedIn: 'root',
})
export class UploadSessionQueryService {
  /**
   * Normalizes free-text values used for safe comparisons.
   * Returns an empty string for null/undefined input.
   */
  private normalizeText(value: unknown): string {
    return value?.toString().trim().toLowerCase() ?? '';
  }

  /**
   * Counts the number of sessions for a given user that are not closed.
   * Comparison for the status is case-insensitive.
   *
   * @param sessions - A readonly array of UploadSessionItem or null.
   * @param user - The user for which to count active sessions.
   * @returns The count of sessions with a status that is not "closed".
   */
  public countActiveSessionsForUser(
    sessions: ReadonlyArray<UploadSessionItem> | null,
    user: string
  ): number {
    // Validate input data
    if (!sessions || !Array.isArray(sessions)) {
      return 0;
    }
    if (!user || typeof user !== 'string') {
      return 0;
    }

    // Filter sessions for the provided user where the status is not 'closed' (case insensitive)
    const normalizedUser = this.normalizeText(user);
    const activeSessions = sessions.filter((item) => {
      // Validate that the required fields exist on the item.
      if (!item || !item.status || !item.user) {
        return false;
      }

      // Ensure the session belongs to the specified user.
      if (this.normalizeText(item.user) !== normalizedUser) {
        return false;
      }

      // Convert status to lowercase and check that it is not "closed".
      return this.normalizeText(item.status) !== 'closed';
    });

    // Return the count of active sessions.
    return activeSessions.length;
  }

  /**
   * Counts only OPEN sessions for a given user.
   * This is used by the "Create New Upload Session" limit validation.
   *
   * @param sessions - A readonly array of UploadSessionItem or null.
   * @param user - The user for which to count OPEN sessions.
   * @returns Number of sessions where status is exactly "OPEN" (case-insensitive).
   */
  public countOpenSessionsForUser(
    sessions: ReadonlyArray<UploadSessionItem> | null,
    user: string
  ): number {
    if (!sessions || !Array.isArray(sessions)) {
      return 0;
    }
    if (!user || typeof user !== 'string') {
      return 0;
    }

    const normalizedUser = this.normalizeText(user);
    return sessions.filter((item) => {
      if (!item || !item.status || !item.user) {
        return false;
      }

      if (this.normalizeText(item.user) !== normalizedUser) {
        return false;
      }

      return this.normalizeText(item.status) === SESSION_STATUS.OPEN.toLowerCase();
    }).length;
  }
}
